import assign from "lodash/assign";
import get from "lodash/get";
import isFunction from "lodash/isFunction";
import superagent from "superagent";

import JwtHelper from "./JwtHelper";

const defaults = {
    isHeartbeat: false,
};

class WebAPIRequest {
    constructor(method, url, options, eventEmitter) {
        this.shouldDeferHeartbeat = false;
        this.options = assign({}, defaults, options);
        this.queryParams = undefined;
        this.sendParams = undefined;
        this.dataType = undefined;
        this.method = method;
        this.url = url;
        this.file = null;

        this.aborted = false;
        this.endCallback = () => {};
        this.ended = false;
        this.isCancelable = true;
        this.request = undefined;
        this.webAPIEventEmitter = eventEmitter;

        this.makeNonMultipartRequest = this.makeNonMultipartRequest.bind(this);
        this.makeMultipartRequest = this.makeMultipartRequest.bind(this);
    }

    query(params) {
        this.queryParams = params;

        return this;
    }

    send(params) {
        this.sendParams = params;

        return this;
    }

    type(type) {
        this.dataType = type;

        return this;
    }

    setFile(file) {
        this.file = file;
        return this;
    }

    /**
     * If multiple ajax calls happen at the same time, we might want to defer
     * the heartbeat so we won't have multiple subsequent state changes (flash
     * of state, lol?).
     */
    deferHeartbeat() {
        this.shouldDeferHeartbeat = true;

        return this;
    }

    cancelable(isCancelable = true) {
        this.isCancelable = isCancelable;

        return this;
    }

    end(callback) {
        this.endCallback = callback;
        const jwtToken = JwtHelper.getJwtToken();
        if (jwtToken && JwtHelper.isJwtTokenExpired(jwtToken)) {
            this.refreshJwtToken();
        } else if (!this.request) {
            this.request = this.makeRequest();
        }
    }

    refreshJwtToken() {
        const jwtToken = JwtHelper.getJwtToken();
        return superagent
            .get("/chat/refreshToken")
            .set("X-Directly-Token", jwtToken)
            .set("X-Insidr-Ajax", "XMLHttpRequest")
            .set("X-Top-Referer", document.referrer)
            .set("Cache-Control", "no-cache")
            .set("Pragma", "no-cache")
            .end((error, response) => {
                if (error) {
                    this.webAPIEventEmitter.emit("session-reset", {});
                    return;
                }
                const newJwtToken = response.header["x-directly-token"];
                if (newJwtToken) {
                    JwtHelper.setJwtToken(newJwtToken);
                }
                this.request = this.makeRequest();
            });
    }

    makeRequest() {
        const requestStrategy = this.file === null ? this.makeNonMultipartRequest : this.makeMultipartRequest;

        this.webAPIEventEmitter.emit("request-start", { request: this });

        return requestStrategy(this.url);
    }

    makeMultipartRequest(url) {
        const jwtToken = JwtHelper.getJwtToken();
        const superAgentRequest = superagent
            .post(url)
            .set(jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {})
            .set("X-Insidr-Ajax", "XMLHttpRequest")
            .set("X-Top-Referer", document.referrer)
            .set("Cache-Control", "no-cache")
            .set("Pragma", "no-cache");

        if (this.sendParams !== undefined) {
            this.sendParams.forEach((value, key) => superAgentRequest.field(key, value));
        }

        return superAgentRequest.attach("file", this.file).end((error, response) => {
            this.setTokenFromResponse(response);
            if (isFunction(this.endCallback)) {
                this.ended = true;
                this.webAPIEventEmitter.emit("request-end");
                this.endCallback.call(this, error, response);
            } else {
                console.warn("Callback must be a function."); // eslint-disable-line no-console
            }
        });
    }

    makeNonMultipartRequest(url) {
        let contentType = "";
        if (this.method === "POST") {
            contentType = this.dataType === "json" ? "application/json" : "application/x-www-form-urlencoded";
        }

        const jwtToken = JwtHelper.getJwtToken();
        return superagent[this.method](url)
            .set(jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {})
            .set("X-Insidr-Ajax", "XMLHttpRequest")
            .set("X-Top-Referer", document.referrer)
            .set("Content-Type", contentType)
            .set("Cache-Control", "no-cache")
            .set("Pragma", "no-cache")
            .query(this.queryParams)
            .send(this.sendParams)
            .end((error, response) => {
                this.setTokenFromResponse(response);
                if (isFunction(this.endCallback)) {
                    this.ended = true;
                    this.webAPIEventEmitter.emit("request-end");
                    this.endCallback.call(this, error, response);
                } else {
                    console.warn("Callback must be a function."); // eslint-disable-line no-console
                }
            });
    }

    abort() {
        if (this.isCancelable) {
            if (this.request) {
                this.request.abort();
            }
            this.aborted = true;
        }
    }

    setTokenFromResponse(response) {
        const jwtToken = get(response, "header[x-directly-token]");
        if (jwtToken) {
            JwtHelper.setJwtToken(jwtToken);
        }
    }

    get isHeartbeat() {
        return this.options.isHeartbeat;
    }
}

export default WebAPIRequest;
